import { EXERCISE_CATEGORY_LABELS, type ExerciseCategory, type TrainingExerciseDto } from '@ukinfo/shared';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button } from '../ui';
import { DiagramFullscreen } from './DiagramFullscreen';
import { DiagramPlayer } from './DiagramPlayer';
import { SketchImageButton } from './ShareImageButton';
import { DiagramLegend } from './PitchDiagram';

/** Dezentes Kennzeichen: Das Modul laeuft als Test. */
export function TestBadge(): JSX.Element {
  return (
    <Badge tone="warning" title="Die Trainingsplanung läuft als Test – Rückmeldungen sind willkommen">
      Test
    </Badge>
  );
}

export function CategoryBadge({ category }: { category: ExerciseCategory }): JSX.Element {
  return <Badge tone="neutral">{EXERCISE_CATEGORY_LABELS[category]}</Badge>;
}

/** Kennzeichen einer Uebung: Standardkatalog und/oder Linkuebung. */
export function SourceBadges({ exercise }: { exercise: Pick<TrainingExerciseDto, 'source' | 'isLink'> & { videos?: unknown[] } }): JSX.Element {
  return (
    <>
      {exercise.source === 'CATALOG' && <Badge tone="brand" title="Aus dem Standardkatalog – nicht änderbar, aber kopierbar">Standard</Badge>}
      {exercise.isLink && <Badge tone="info" title="Verweist auf eine Seite im Netz">Link</Badge>}
      {(exercise.videos?.length ?? 0) > 0 && <Badge tone="info" title="Zu dieser Übung gibt es Videos">Video</Badge>}
    </>
  );
}

function TextBlock({ title, text }: { title: string; text: string }): JSX.Element | null {
  if (!text.trim()) return null;
  return (
    <section className="print-avoid-break">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-700">{text}</p>
    </section>
  );
}

/** Beschreibung einer Uebung: Organisation, Ablauf, Coaching, Varianten, Material. */
export function ExerciseText({
  exercise,
  compact,
}: {
  exercise: Pick<TrainingExerciseDto, 'setup' | 'procedure' | 'coaching' | 'variations' | 'material'>;
  /** Fuer die Druckansicht: ohne Varianten. */
  compact?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <TextBlock title="Organisation" text={exercise.setup} />
      <TextBlock title="Ablauf" text={exercise.procedure} />
      <TextBlock title="Coaching" text={exercise.coaching} />
      {!compact && <TextBlock title="Varianten" text={exercise.variations} />}
      <TextBlock title="Material" text={exercise.material} />
    </div>
  );
}

/**
 * Skizze zum Abspielen mit Legende; fehlt sie, bleibt der Platz leer. Ohne Pfeile gibt es keine
 * Abspielleiste. "Vollbild" zeigt die Skizze bildschirmfuellend, mit Zoom und Schwenken (Handy).
 */
export function SketchWithLegend({ exercise, className }: { exercise: Pick<TrainingExerciseDto, 'diagram' | 'title'>; className?: string }): JSX.Element | null {
  const [fullscreen, setFullscreen] = useState(false);
  if (!exercise.diagram) return null;
  return (
    <div className={className}>
      <div className="mb-1 flex flex-wrap justify-end gap-2 print:hidden">
        <SketchImageButton diagram={exercise.diagram} title={exercise.title} />
        <Button type="button" variant="secondary" size="sm" aria-label="Vollbild" title="Skizze im Vollbild ansehen" onClick={() => setFullscreen(true)}>
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
          </svg>
          Vollbild
        </Button>
      </div>
      <DiagramPlayer diagram={exercise.diagram} title={exercise.title} className="mx-auto max-h-[60vh] max-w-md" />
      <DiagramLegend diagram={exercise.diagram} className="mt-2" />
      {fullscreen && <DiagramFullscreen diagram={exercise.diagram} title={exercise.title} onClose={() => setFullscreen(false)} />}
    </div>
  );
}

/** Link im Aussehen eines zweitrangigen Knopfes. */
export function LinkButton({ to, children }: { to: string; children: ReactNode }): JSX.Element {
  return (
    <Link
      to={to}
      className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:border-slate-400 hover:bg-slate-50 sm:min-h-0"
    >
      {children}
    </Link>
  );
}

/** Link zu einer Linkuebung - in neuem Tab, ohne Weitergabe der Herkunft. */
export function ExternalLink({ href, children }: { href: string; children: ReactNode }): JSX.Element {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="break-all font-medium text-brand-700 underline">
      {children}
    </a>
  );
}
